import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react'
import type { ISODate, Task } from '@app/shared'

import { useDeleteTask, useUpdateTask, type TaskPatch } from '../../lib/data'
import type { PeekAnchor } from '../../lib/popover'
import { ContextMenu } from '../ContextMenu'
import { UNFILED } from './derive'
import { PhoneMayMissNote } from './PhoneMayMissNote'
import {
  IN_ONE_HOUR_LABEL,
  REPEAT_OPTIONS,
  TIME_PRESETS,
  inOneHour,
  isPickableRepeat,
  repeatHint,
} from './presets'

// ============================================================================
// THE ROW MENU — set a due date, move the task to another list, delete it.
//
// WHY IT EXISTS. Until now a task could only be created and ticked: there was no way, anywhere
// in the product, to give a task a date or move it between lists after the fact, and no way to
// remove one that should never have been written down (owner report, 2026-08-28). Quick-add
// still parses nothing out of a title — no date is read from the words, no list is guessed —
// so this menu is the manual way, and manual is the only way there is.
//
// A POPOVER, NOT A DIALOG, and this file no longer says where one goes. `role="dialog"` WITHOUT
// `aria-modal` (it traps no focus), light dismiss on a pointer outside it, Escape closes, and the
// zoom-aware placement that fixed the owner's "half off the screen" report all live one level up,
// in components/ContextMenu.tsx. That primitive IS `lib/popover.ts` — `placeAnchored`, `rootZoom`,
// `useDismissable`, the same pure functions unit-tested in lib/popover.test.ts, with the same one
// division on the way out — plus two things a second right-click menu (the chat thread list)
// needed and this one wanted anyway:
//
//   · A RE-PLACE WHEN THE PANEL'S OWN HEIGHT CHANGES. This menu's list picker grows with however
//     many lists exist, so a placement keyed on the anchor alone leaves a taller panel hanging off
//     the bottom of the screen — which is why this file used to key its own layout effect on
//     `lists.length`. The primitive keys on its `children` instead: a superset, and one that does
//     not require it to know what its callers put inside it.
//   · ONE OPEN AT A TIME ACROSS COMPONENTS, so right-clicking straight from a task row onto a
//     chat thread row cannot leave two panels standing.
//
// TWO WAYS IN, ONE MENU. The `...` trigger at the end of the row opens it anchored to the button;
// right-clicking anywhere on the row opens the SAME panel anchored at the pointer — a cursor is
// just a zero-sized rect (`anchorAtPoint`). There is no second menu with a subset of these
// controls: a right-click that offers less than the button beside it punishes the owner for
// knowing the shortcut.
//
// EVERY CONTROL IS A REAL FORM CONTROL. The date is `<input type="date">` and the list is a
// `<select>` — the same argument components/journal/IndexPanels.tsx makes for its month chip: a
// hand-rolled calendar or listbox would have to re-earn keyboard support, typeahead and the
// phone's native pickers, and it would get them wrong first.
//
// DELETE IS IMMEDIATE. It asked once until 2026-08-28; the owner's answer was "they should just
// delete", and he is right — the confirmation was a second decision about a decision already made,
// and there is no bulk delete here that could take a whole board out by accident. What it costs is
// real and named: there is no undo for it, and the row does not go to the done log.
//
// EVERY ACTION CLOSES THE MENU. A due date change moves the row to a different card, so leaving
// the panel open would leave it floating beside a row that is no longer under it. It also means
// the panel never holds a stale copy of a task the refetch has since changed.
// ============================================================================

/** The rectangle the menu is drawn beside — captured once, at click time. */
export type MenuAnchor = PeekAnchor

interface TaskMenuApi {
  /** The task whose menu is open, or null. ONE AT A TIME is the whole reason this is state. */
  openId: string | null
  open: (task: Task, anchor: MenuAnchor) => void
  close: () => void
  /** Set when writes cannot happen — the trigger then says why instead of opening a dead panel. */
  disabledReason?: string
}

const TaskMenuContext = createContext<TaskMenuApi | null>(null)

/**
 * The rows reach the menu through context rather than through props threaded across five call
 * sites of `FlatRows`. The provider owns the "only one open" rule, because that rule cannot live
 * in a row: a row only knows about itself.
 */
export function useTaskMenu(): TaskMenuApi | null {
  return useContext(TaskMenuContext)
}

export function TaskMenuHost({
  lists,
  today,
  disabledReason,
  children,
}: {
  /** The list names the board already knows about. Nothing invents one. */
  lists: string[]
  /** The board's own today — the day a clock preset lands on when the row has none. */
  today?: ISODate
  disabledReason?: string
  children: ReactNode
}) {
  const [open, setOpen] = useState<{ task: Task; anchor: MenuAnchor } | null>(null)
  const update = useUpdateTask()
  const remove = useDeleteTask()

  const close = useCallback(() => setOpen(null), [])
  const api = useMemo<TaskMenuApi>(
    () => ({
      openId: open?.task.id ?? null,
      open: (task, anchor) => setOpen({ task, anchor }),
      close,
      disabledReason,
    }),
    [open, close, disabledReason],
  )

  return (
    <TaskMenuContext.Provider value={api}>
      {children}
      {open && (
        <TaskEditMenu
          key={open.task.id}
          task={open.task}
          anchor={open.anchor}
          lists={lists}
          today={today}
          onDue={(due) => {
            update.mutate({ id: open.task.id, patch: { due } })
            close()
          }}
          onPatch={(patch) => {
            update.mutate({ id: open.task.id, patch })
            close()
          }}
          onList={(list) => {
            update.mutate({ id: open.task.id, patch: { list } })
            close()
          }}
          onDelete={() => {
            remove.mutate(open.task.id)
            close()
          }}
          onClose={close}
        />
      )}
    </TaskMenuContext.Provider>
  )
}

const WIDTH = 236

function TaskEditMenu({
  task,
  anchor,
  lists,
  today,
  onDue,
  onPatch,
  onList,
  onDelete,
  onClose,
}: {
  task: Task
  anchor: MenuAnchor
  lists: string[]
  today?: ISODate
  onDue: (due: ISODate | null) => void
  /** A reminder time or a repeat — either may need to date the row as well, so it is one patch. */
  onPatch: (patch: TaskPatch) => void
  onList: (list: string) => void
  onDelete: () => void
  onClose: () => void
}) {
  /**
   * THE ROW'S OWN HOUR, as 'HH:MM' local, for the time field's value. `reminderAt` is an instant;
   * the field wants the wall clock the owner would recognise, so it is read in the browser's
   * zone — the same zone `new Date(`${due}T${hhmm}`)` below writes it back in.
   */
  const reminderHhmm = (() => {
    if (!task.reminderAt) return ''
    const at = new Date(task.reminderAt)
    if (Number.isNaN(at.getTime())) return ''
    return `${String(at.getHours()).padStart(2, '0')}:${String(at.getMinutes()).padStart(2, '0')}`
  })()

  /** A clock on the row: the row's day if it has one, else the board's today, else nowhere. */
  function remindAt(hhmm: string, day: ISODate | null = task.due ?? today ?? null) {
    if (!day) return
    const at = new Date(`${day}T${hhmm}`)
    if (Number.isNaN(at.getTime())) return
    onPatch({ due: day, reminderAt: at.toISOString() })
  }

  /** A rule the picker cannot show (INTERVAL, BYDAY) is kept as its own option, never clobbered. */
  const repeatOptions = isPickableRepeat(task.rrule)
    ? REPEAT_OPTIONS
    : [...REPEAT_OPTIONS, { label: task.rrule!, rrule: task.rrule }]
  const repeatWord = repeatHint(task.rrule)
  const canRemind = task.due !== null || today !== undefined
  // The task's own list is always offerable even if the board has stopped counting it — a task
  // filed under a list nobody else uses must not lose its own name from the picker.
  const options = [UNFILED, ...lists.filter((l) => l !== UNFILED)]
  if (!options.includes(task.list)) options.push(task.list)

  return (
    <ContextMenu anchor={anchor} label={`Edit ${task.title}`} width={WIDTH} onClose={onClose}>
      <div className="truncate text-[11.5px] font-semibold leading-[1.3] text-ink">
        {task.title}
      </div>

      <label className="flex flex-col gap-[5px]">
        <span className="text-[9.5px] font-semibold uppercase leading-none tracking-[0.08em] text-microcopy">
          Due date
        </span>
        <input
          type="date"
          value={task.due ?? ''}
          onChange={(e) => {
            const value = e.currentTarget.value
            // An emptied field is a CLEARED date, not a no-op: `null` travels all the way to the
            // Worker's PATCH, which reads it as "remove the day". A blank that meant nothing would
            // make the field impossible to undo from.
            onDue(value ? (value as ISODate) : null)
          }}
          className="mo-field w-full rounded-context border border-line bg-surface px-[9px] py-[6px] text-[12px] font-medium leading-none text-ink-2"
        />
      </label>

      {/* Offered only when there is a date to remove. A `Clear` that clears nothing is a control
          that lies about the state of the row it belongs to. */}
      {task.due !== null && (
        <button
          type="button"
          onClick={() => onDue(null)}
          className="self-start text-[11px] font-semibold leading-none text-ink-3 underline underline-offset-2 transition-colors duration-[var(--dur-state)] ease-[var(--ease-ink)] hover:text-ink"
        >
          Clear the date
        </button>
      )}

      {/* REMIND AT (owner, 2026-09-14). The same four presets the quick-add offers, plus the
          exact field. A preset on an undated row dates it too — the board's today — because a
          reminder is a clock on a day, and the hint below says which day that will be. */}
      <div className="flex flex-col gap-[6px]">
        <span className="text-[9.5px] font-semibold uppercase leading-none tracking-[0.08em] text-microcopy">
          Remind at
        </span>
        <div className="flex flex-wrap items-center gap-[5px]">
          <MenuChip
            active={false}
            disabled={false}
            onClick={() => {
              const at = inOneHour()
              remindAt(at.hhmm, at.date)
            }}
          >
            {IN_ONE_HOUR_LABEL}
          </MenuChip>
          {TIME_PRESETS.map((p) => (
            <MenuChip
              key={p.hhmm}
              active={reminderHhmm === p.hhmm}
              disabled={!canRemind}
              onClick={() => remindAt(p.hhmm)}
            >
              {p.label}
            </MenuChip>
          ))}
        </div>
        <input
          type="time"
          value={reminderHhmm}
          disabled={!canRemind}
          onChange={(e) => {
            const value = e.currentTarget.value
            if (value) remindAt(value)
            // An emptied field is a CLEARED reminder; the day stays.
            else onPatch({ reminderAt: null })
          }}
          aria-label="Remind me at"
          className="mo-field w-full rounded-context border border-line bg-surface px-[9px] py-[6px] text-[12px] font-medium leading-none text-ink-2 disabled:opacity-60"
        />
        {task.due === null && (
          <p className="text-[10.5px] leading-[1.4] text-microcopy text-pretty">
            {today ? `A time puts it on today, ${today.slice(5)}.` : 'Give it a date first.'}
          </p>
        )}
        {/* The menu closes on the write, so this reads the row's SAVED time: reopened within
            15 minutes of it, off the phone app, the row says the phone may not have heard. */}
        <PhoneMayMissNote reminderAt={task.reminderAt} className="text-[10.5px]" />
      </div>

      <label className="flex flex-col gap-[5px]">
        <span className="text-[9.5px] font-semibold uppercase leading-none tracking-[0.08em] text-microcopy">
          Repeat
        </span>
        <select
          value={task.rrule ?? ''}
          onChange={(e) => {
            const rrule = e.currentTarget.value || null
            // A repeat counts from `due`; an undated row that starts repeating starts today.
            const patch: TaskPatch = { rrule }
            if (rrule && task.due === null && today) patch.due = today
            onPatch(patch)
          }}
          aria-label="Repeat"
          className="w-full rounded-context border border-line bg-surface px-[9px] py-[6px] text-[12px] font-medium leading-none text-ink-2"
        >
          {repeatOptions.map((o) => (
            <option key={o.label} value={o.rrule ?? ''}>
              {o.label}
            </option>
          ))}
        </select>
        <p className="text-[10.5px] leading-[1.4] text-microcopy text-pretty">
          {repeatWord
            ? `${repeatWord[0]!.toUpperCase()}${repeatWord.slice(1)} — the next one is written when this one is ticked.`
            : 'A repeat makes the next one the moment you complete this one.'}
        </p>
      </label>

      <label className="flex flex-col gap-[5px]">
        <span className="text-[9.5px] font-semibold uppercase leading-none tracking-[0.08em] text-microcopy">
          List
        </span>
        <select
          value={task.list}
          onChange={(e) => onList(e.currentTarget.value)}
          className="w-full rounded-context border border-line bg-surface px-[9px] py-[6px] text-[12px] font-medium leading-none text-ink-2"
        >
          {options.map((name) => (
            <option key={name} value={name}>
              {name}
            </option>
          ))}
        </select>
      </label>
      <p className="text-[10.5px] leading-[1.4] text-microcopy text-pretty">
        The lists are the ones your tasks are already filed under. Inbox is the unfiled one.
      </p>

      {/* NO CONFIRMATION (owner, 2026-08-28: "they should just delete"). One press removes the
          row and closes the panel. The step this used to interpose was a second decision about a
          decision already made, on a screen whose whole argument is that a reminders app should be
          boring — and the owner pays that toll on every stray row he ever typed. The sentence it
          used to print ("it does not go to the done log") is now on the button's own tooltip,
          where it informs without standing in the way. */}
      <div className="mt-[2px] border-t border-fill pt-[9px]">
        <button
          type="button"
          onClick={onDelete}
          title="Deleted straight away. It does not go to the done log — it is gone."
          className="text-[11px] font-semibold leading-none text-danger-2 transition-colors duration-[var(--dur-state)] ease-[var(--ease-ink)] hover:text-danger"
        >
          Delete task
        </button>
      </div>
    </ContextMenu>
  )
}

/** A preset chip inside the menu. Same words as the quick-add's; sized for a 236px panel. */
function MenuChip({
  active,
  disabled,
  onClick,
  children,
}: {
  active: boolean
  disabled: boolean
  onClick: () => void
  children: string
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-pressed={active}
      className={[
        'flex h-[22px] flex-none items-center rounded-pill border px-[8px] text-[10.5px] font-medium leading-none',
        'disabled:cursor-not-allowed disabled:opacity-50',
        active ? 'border-ink bg-ink text-surface' : 'border-line bg-surface text-ink-2 hover:bg-fill-2',
      ].join(' ')}
    >
      {children}
    </button>
  )
}
